import React, { useEffect, useState } from "react";
import { api, formatMoney, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Banknote } from "lucide-react";

const CURRENCIES = ["usd", "eur", "gbp", "inr", "aed", "sgd"];

export default function Withdrawals() {
  const [items, setItems] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("usd");
  const [method, setMethod] = useState("bank");
  const [destination, setDestination] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const [w, l] = await Promise.all([api.get("/wallet"), api.get("/withdrawals")]);
      setWallet(w.data);
      setItems(l.data);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const parsed = parseFloat(amount);
    if (!parsed || parsed <= 0) { toast.error("Enter a valid amount"); return; }
    if ((destination || "").length < 4) { toast.error("Destination must be at least 4 characters"); return; }
    setLoading(true);
    try {
      await api.post("/withdrawals", { amount: parsed, currency, method, destination });
      toast.success("Withdrawal requested");
      setAmount(""); setDestination("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const balance = wallet?.balances?.[currency] ?? 0;

  return (
    <main data-testid="withdrawals-page" className="max-w-5xl mx-auto px-6 lg:px-10 py-16">
      <div className="text-xs uppercase tracking-[0.3em] text-gold">Treasury</div>
      <h1 className="font-display text-5xl tracking-tighter mt-3">Withdraw funds</h1>
      <p className="text-secondary-fg mt-3 max-w-lg">Move balance from your y ℶα wallet to a connected bank or card.</p>

      <div className="grid grid-cols-12 gap-8 mt-14">
        <div className="col-span-12 lg:col-span-7">
          <form onSubmit={submit} className="space-y-8">
            <div className="grid grid-cols-12 gap-6">
              <div className="col-span-8">
                <label className="text-xs uppercase tracking-[0.25em] text-muted-fg">Amount</label>
                <input data-testid="wd-amount" type="number" min="0.01" step="0.01" required value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" className="lux-input mt-2 font-display text-3xl" />
              </div>
              <div className="col-span-4">
                <label className="text-xs uppercase tracking-[0.25em] text-muted-fg">Currency</label>
                <select data-testid="wd-currency" value={currency} onChange={(e) => setCurrency(e.target.value)} className="lux-input mt-2 uppercase">
                  {CURRENCIES.map((c) => <option key={c} value={c} className="bg-bg">{c.toUpperCase()}</option>)}
                </select>
              </div>
            </div>
            <div className="text-xs text-muted-fg">Available: <span className="text-gold font-mono">{formatMoney(balance, currency)}</span></div>

            <div>
              <label className="text-xs uppercase tracking-[0.25em] text-muted-fg">Destination</label>
              <div className="grid grid-cols-12 gap-3 mt-2">
                <div className="col-span-4 grid grid-cols-2 gap-2">
                  {["bank","card"].map((m) => (
                    <button key={m} type="button" data-testid={`wd-method-${m}`} onClick={() => setMethod(m)} className={`py-3 border text-xs uppercase tracking-widest transition-all ${method === m ? "border-gold text-gold bg-[rgba(212,175,55,0.05)]" : "hairline text-secondary-fg hover:text-white"}`}>
                      {m}
                    </button>
                  ))}
                </div>
                <div className="col-span-8">
                  <input data-testid="wd-destination" value={destination} onChange={(e) => setDestination(e.target.value)} placeholder={method === "bank" ? "Account number" : "Card number"} className="lux-input" />
                </div>
              </div>
            </div>

            <button data-testid="wd-submit" disabled={loading} className="btn-gold inline-flex items-center gap-2">
              <Banknote size={14} /> {loading ? "Submitting…" : "Withdraw"}
            </button>
          </form>
        </div>

        <div className="col-span-12 lg:col-span-5">
          <div className="lux-card p-8">
            <div className="text-xs uppercase tracking-widest text-muted-fg">Settlement</div>
            <div className="font-display text-2xl mt-3">Discreet · 1–3 business days</div>
            <div className="text-sm text-secondary-fg mt-4 leading-relaxed">Funds settle to your destination after compliance review. You'll receive a notification when complete.</div>
          </div>
        </div>
      </div>

      <section className="mt-16">
        <h2 className="font-display text-3xl tracking-tight mb-6">History</h2>
        {items.length === 0 ? (
          <div className="border hairline p-12 text-center text-secondary-fg">No withdrawals yet.</div>
        ) : (
          <div className="border hairline">
            <div className="grid grid-cols-12 px-6 py-3 border-b hairline text-[10px] uppercase tracking-[0.25em] text-muted-fg">
              <div className="col-span-5">Destination</div>
              <div className="col-span-3">Status</div>
              <div className="col-span-4 text-right">Amount</div>
            </div>
            {items.map((w) => (
              <div key={w.withdrawal_id} data-testid={`wd-row-${w.withdrawal_id}`} className="grid grid-cols-12 px-6 py-5 border-b hairline last:border-b-0 hover:bg-surface transition-colors">
                <div className="col-span-5 text-sm">
                  <div className="capitalize">{w.method} •••• {w.destination_last4}</div>
                  <div className="text-xs text-muted-fg mt-1">{new Date(w.created_at).toLocaleString()}</div>
                </div>
                <div className="col-span-3 self-center">
                  <span className="text-xs uppercase tracking-widest text-gold">{w.status}</span>
                </div>
                <div className="col-span-4 text-right font-display text-xl">
                  − {formatMoney(w.amount, w.currency)}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
